/**
 * Public (unauthenticated) JSON API calls: same network logging / sendRequest pipeline as BaseApiService,
 * without Authorization header; callers use skipSessionFailureOnAuthError where anonymous routes may return 401.
 */

import type { ApiPayload } from "../types";
import { buildPublicApiJsonHeaders } from "./headerHelpers";
import { fetchWithNetworkLogging, sendRequest } from "./requestHelpers";

function resolveUrlString(url: string | URL): string {
	return typeof url === "string" ? url : url.href;
}

/**
 * Returns raw `Response` after the same transport as authenticated API (credentials, JSON headers, network logging).
 * Use when callers need `response.ok` branching or non-JSON bodies on errors.
 */
export async function publicJsonFetchRaw(
	url: string | URL,
	init: {
		method: "GET" | "POST";
		body?: unknown;
		credentials?: RequestCredentials;
		cache?: RequestCache;
		invokeUserFacingErrorHandler?: boolean;
	},
): Promise<Response> {
	const urlString = resolveUrlString(url);
	const headers = buildPublicApiJsonHeaders();
	const requestInit: RequestInit = {
		method: init.method,
		headers,
		credentials: init.credentials ?? "include",
		cache: init.cache ?? "no-store",
	};
	if (init.body !== undefined) {
		requestInit.body = JSON.stringify(init.body);
	}
	return fetchWithNetworkLogging(urlString, requestInit, {
		invokeUserFacingErrorHandler: init.invokeUserFacingErrorHandler !== false,
	});
}

/**
 * Parses successful JSON responses through `processResponse` (same as `makeHttpRequest` for OK/error handling).
 * HTTP errors throw the same payload shape as `BaseApiService` (`handleErrorResponse`).
 */
export async function makePublicHttpRequest<T>(
	url: string | URL,
	method: "GET" | "POST",
	body?: ApiPayload,
	requestCredentials: RequestCredentials = "include",
): Promise<T> {
	const urlString = resolveUrlString(url);
	const request = async (): Promise<Response> => {
		const headers = buildPublicApiJsonHeaders();
		const requestInit: RequestInit = {
			method,
			headers,
			credentials: requestCredentials,
			cache: "no-store",
		};
		if (method === "POST" && body !== undefined) {
			requestInit.body = JSON.stringify(body);
		}
		return fetchWithNetworkLogging(urlString, requestInit);
	};
	return sendRequest<T>(request, { skipSessionFailureOnAuthError: true });
}

/**
 * POST multipart (public verify-document, etc.): no JSON Content-Type; browser sets boundary.
 */
export async function publicFormDataPostRaw(url: string | URL, formData: FormData): Promise<Response> {
	const urlString = resolveUrlString(url);
	return fetchWithNetworkLogging(urlString, {
		method: "POST",
		body: formData,
		credentials: "include",
		cache: "no-store",
	});
}
